import _ from 'lodash';
import { SelectionAreaWrapper } from '@/shared/ui';
import { ID_DIV_STRIP_RHYTHM_EVENT } from '@/entities/event';
import { eventOffsetPreviewStrip } from '@/entities/strip';
import RhythmEventPreviewStrip from '@/features/event-strip-preview';
import useStripEvents from './useStripEvents';

export type StripEventsProps = {
  activeButton: any;
  studyId: string;
  profileId: string;
  timezoneOffset: number;
  startReport: any;
  ecgDataMap: any;
};

/**
 * Renders the scrollable grid of rhythm event preview strips for the ECG events tab, inside a rubber-band selection area. Reads events, thumbnail sizing and ECG data from useStripEvents, and marks each strip active when it matches the selected strip or selected when the drag selection includes its id.
 *
 * @param props - Study, profile and ECG context forwarded to useStripEvents.
 * @returns The selection-area wrapper containing one preview strip per rhythm event.
 */
const StripEvents = ({
  activeButton: activeButtonProp,
  studyId,
  profileId,
  timezoneOffset: timezoneOffsetProp,
  startReport: startReportProp,
  ecgDataMap: ecgDataMapProp,
}: StripEventsProps) => {
  const {
    keyRecoil,
    activeButton,
    timezoneOffset,
    startReport,
    ecgDataMap,
    selectionAreaWrapperRef,
    holterEvents,
    isLoadingStrip,
    selectedStrip,
    setSelectedEvent,
    handleClickSelectEvent,
    isHasSelected,
    highPassThumbnail,
    gainThumbnail,
    channelsThumbnail,
    sizeThumbnailConfig,
  } = useStripEvents({
    activeButton: activeButtonProp,
    studyId,
    profileId,
    timezoneOffset: timezoneOffsetProp,
    startReport: startReportProp,
    ecgDataMap: ecgDataMapProp,
  });

  return (
    <SelectionAreaWrapper
      ref={selectionAreaWrapperRef}
      id={ID_DIV_STRIP_RHYTHM_EVENT}
      className="strips-container --min-width-strip-event"
      selectables=".single-strip-event"
      handleSetSelected={setSelectedEvent}
      defaultSelectedId={(selectedStrip as any)?.idEvent}
      isNoData={holterEvents?.length === 0}
      isLoading={isLoadingStrip}
    >
      {(selected) => (
        <div className="strips-img-container">
          {_.map(holterEvents, (event, index) => (
            <RhythmEventPreviewStrip
              keyRecoil={keyRecoil}
              activeButton={activeButton}
              key={event.idEvent}
              index={index}
              id={`beat-${event.idEvent}`}
              isActive={event.idEvent === (selectedStrip as any)?.idEvent}
              isSelected={selected.has(`beat-${event.idEvent}`) && isHasSelected}
              event={event}
              ecgDataMap={ecgDataMap}
              highPass={highPassThumbnail}
              gain={gainThumbnail}
              channels={channelsThumbnail}
              width={sizeThumbnailConfig.width}
              height={sizeThumbnailConfig.height}
              customOffset={eventOffsetPreviewStrip}
              timezoneOffset={timezoneOffset}
              startReport={startReport}
              onClickSelectEvent={handleClickSelectEvent}
            />
          ))}
        </div>
      )}
    </SelectionAreaWrapper>
  );
};

export default StripEvents;
